import React, { useState, useRef, useEffect } from 'react';
import { Card } from '../components/Card';
import { Header } from '../components/Header';
import { Icon } from '../components/Icon';
import { Button } from '../components/Button';
import { apiClient } from '../api/client';
import { useTranslation } from 'react-i18next';
import { PremiumFeature } from '../components/PremiumFeature';
import './AIChatScreen.css';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isOffTopic?: boolean;
  followUpSuggestions?: string[];
}

// Ключи для suggestions — как в mobile
const SUGGESTION_KEYS = [
  'aiChat.suggestions.balance',
  'aiChat.suggestions.monthSpent',
  'aiChat.suggestions.todayBudget',
  'aiChat.suggestions.compare',
  'aiChat.suggestions.save',
  'aiChat.suggestions.goal',
  'aiChat.suggestions.limits',
  'aiChat.suggestions.topExpenses',
] as const;

// Функция для форматирования Markdown в сообщениях
const formatMessage = (text: string): React.ReactNode => {
  if (!text) return null;

  // Разбиваем на строки
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];

  lines.forEach((line, lineIndex) => {
    // Обрабатываем жирный текст **текст**
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const boldRegex = /\*\*(.*?)\*\*/g;
    let match;

    while ((match = boldRegex.exec(line)) !== null) {
      // Добавляем текст до жирного
      if (match.index > lastIndex) {
        parts.push(line.substring(lastIndex, match.index));
      }
      // Добавляем жирный текст
      parts.push(<strong key={`bold-${lineIndex}-${match.index}`}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }

    // Добавляем оставшийся текст
    if (lastIndex < line.length) {
      parts.push(line.substring(lastIndex));
    }

    // Если ничего не нашли, просто добавляем строку
    if (parts.length === 0) {
      parts.push(line);
    }

    // Добавляем строку с переносом
    result.push(
      <span key={`line-${lineIndex}`}>
        {parts}
        {lineIndex < lines.length - 1 && <br />}
      </span>
    );
  });

  return <>{result}</>;
};

export const AIChatScreen: React.FC = () => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Загружаем историю чата при монтировании
    loadChatHistory();
  }, []);

  const loadChatHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const response = await apiClient.getAIChatHistory(50);
      
      if (response.success && response.messages.length > 0) {
        // Преобразуем загруженные сообщения в формат компонента
        const loadedMessages: Message[] = response.messages.map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.timestamp),
          isOffTopic: msg.isOffTopic
        }));
        
        setMessages(loadedMessages);
        console.log(`✅ Loaded ${loadedMessages.length} messages from history`);
      } else {
        // Если истории нет, добавляем приветственное сообщение
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: t('aiChat.welcome'),
          timestamp: new Date()
        }]);
      }
    } catch (err) {
      console.error('Failed to load chat history:', err);
      // При ошибке добавляем приветственное сообщение
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: t('aiChat.welcome'),
        timestamp: new Date()
      }]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getDeviceLanguage = () => {
    const nav = typeof navigator !== 'undefined' ? navigator.language : 'ru';
    return (nav || 'ru').split('-')[0];
  };

  const sendMessageDirect = async (text: string) => {
    const messageText = text.trim();
    if (!messageText || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    try {
      const deviceLang = getDeviceLanguage();
      const response = await apiClient.sendAIChatMessage(
        messageText,
        deviceLang,
        undefined, // dbLanguage - будет из settings при необходимости
        false
      );

      if (response.success) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message,
          timestamp: new Date(),
          isOffTopic: response.is_off_topic,
          followUpSuggestions: response.follow_ups && response.follow_ups.length > 0
            ? response.follow_ups
            : undefined,
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Проверяем на специфические ошибки
        const errorMessage = response.message || t('aiChat.error');
        
        // Если это ошибка квоты, показываем специальное сообщение
        if ((response as any).is_quota_error || (response as any).error_code === 'quota_exceeded') {
          setError(errorMessage);
          alert(errorMessage); // Показываем alert для важных ошибок
        } else {
          setError(errorMessage);
        }
      }
    } catch (err: any) {
      console.error('AI Chat error:', err);
      
      // Проверяем ответ на наличие специфических ошибок
      const errorResponse = err?.response?.data || err?.data;
      if (errorResponse?.is_quota_error || errorResponse?.error_code === 'quota_exceeded') {
        const quotaMessage = errorResponse?.message || 'Превышен лимит использования AI-ассистента. Пожалуйста, попробуйте позже.';
        setError(quotaMessage);
        alert(quotaMessage);
      } else {
        setError(err?.message || t('aiChat.error'));
      }
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSendMessage = () => sendMessageDirect(inputMessage);

  const handleSendSuggestion = (suggestionText: string) => {
    if (isRecording) return;
    sendMessageDirect(suggestionText);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearHistory = async () => {
    if (!confirm(t('aiChat.clearHistoryConfirm'))) return;

    try {
      setIsLoading(true);
      await apiClient.clearAIChatHistory();
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: t('aiChat.welcome'),
        timestamp: new Date()
      }]);
      console.log('✅ Chat history cleared');
    } catch (err) {
      console.error('Error clearing history:', err);
      setError(t('aiChat.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  // Запись голоса
  const startVoiceRecording = async () => {
    try {
      console.log('🎤 Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('✅ Microphone access granted');
      
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ];
      
      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          console.log('✅ Using MIME type:', mimeType);
          break;
        }
      }
      
      if (!selectedMimeType) {
        throw new Error('No supported audio format found');
      }
      
      const recorder = new MediaRecorder(stream, { mimeType: selectedMimeType });
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        console.log('⏹️ Recording stopped');
        const audioBlob = new Blob(chunks, { type: selectedMimeType });
        
        if (audioBlob.size === 0) {
          console.error('❌ Audio blob is empty!');
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        
        try {
          setIsTranscribing(true);
          console.log('📤 Sending audio to backend...');
          const response = await apiClient.transcribeVoice(audioBlob);
          
          if (response.success && response.text) {
            console.log('✅ Transcription successful:', response.text);
            setInputMessage(response.text);
            // Автоматически фокусируем на поле ввода
            if (inputRef.current) {
              inputRef.current.focus();
            }
          } else {
            setError('Не удалось распознать речь');
          }
        } catch (error: any) {
          console.error('❌ Voice transcription error:', error);
          setError(`Ошибка: ${error.message || 'Неизвестная ошибка'}`);
        } finally {
          setIsTranscribing(false);
        }

        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error: any) {
      console.error('❌ Error accessing microphone:', error);
      alert(`Не удалось получить доступ к микрофону: ${error.message || 'Неизвестная ошибка'}`);
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorder && isRecording) {
      if (mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
      setIsRecording(false);
    }
  };

  return (
    <PremiumFeature featureId="ai_chat">
      <div className="ai-chat-screen">
      <Header
        title={t('aiChat.title') || 'AI Помощник'}
        rightAction={
          <button className="neo-raised" onClick={handleClearHistory} style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-danger)'
          }}>
            <Icon name="trash" size={20} />
          </button>
        }
      />

      <div className="ai-chat-messages">
        {isLoadingHistory ? (
          <div className="ai-chat-message ai-chat-message--assistant">
            <div className="ai-chat-message__avatar">
              <Icon name="message-circle" size={16} />
            </div>
            <div className="ai-chat-message__content">
              <div className="ai-chat-message__bubble ai-chat-typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, idx) => {
              const isLastAssistantMessage =
                message.role === 'assistant' &&
                !messages.slice(idx + 1).some((m) => m.role === 'assistant');

              return (
                <div
                  key={message.id}
                  className={`ai-chat-message ${message.role === 'user' ? 'ai-chat-message--user' : 'ai-chat-message--assistant'}`}
                >
                  {message.role === 'assistant' && (
                    <div className="ai-chat-message__avatar">
                      <Icon name="message-circle" size={16} />
                    </div>
                  )}
                  <div className="ai-chat-message__content">
                    <div className="ai-chat-message__bubble">
                      {formatMessage(message.content)}
                    </div>
                    {message.followUpSuggestions &&
                      isLastAssistantMessage &&
                      !isLoading &&
                      message.followUpSuggestions.map((text, i) => (
                        <button
                          key={i}
                          type="button"
                          className="ai-chat-follow-up"
                          onClick={() => handleSendSuggestion(text)}
                          disabled={isLoading}
                        >
                          {text}
                        </button>
                      ))}
                    <div className="ai-chat-message__time">
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                </div>
              );
            })}

            {isLoading && (
              <div className="ai-chat-message ai-chat-message--assistant">
                <div className="ai-chat-message__avatar">
                  <Icon name="message-circle" size={16} />
                </div>
                <div className="ai-chat-message__content">
                  <div className="ai-chat-message__bubble ai-chat-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="ai-chat-error">
                <Icon name="alert-circle" size={16} />
                <span>{error}</span>
              </div>
            )}
          </>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="ai-chat-input-container">
        {/* Suggestions — выше input, как в mobile */}
        <div className="ai-chat-suggestions">
          {SUGGESTION_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className="ai-chat-suggestion"
              onClick={() => handleSendSuggestion(t(key))}
              disabled={isLoading || isRecording}
            >
              {t(key)}
            </button>
          ))}
        </div>

        <Card padding="medium" className="ai-chat-input-card neo-inset">
          <div className="ai-chat-input">
            <textarea
              ref={inputRef}
              className="ai-chat-input__textarea"
              placeholder={t('aiChat.placeholder', 'Введите сообщение...')}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              rows={1}
              maxLength={500}
              disabled={isLoading || isRecording}
            />
            {isTranscribing ? (
              <div className="voice-wave-indicator">
                <div className="voice-wave-bar"></div>
                <div className="voice-wave-bar"></div>
                <div className="voice-wave-bar"></div>
                <div className="voice-wave-bar"></div>
                <div className="voice-wave-bar"></div>
              </div>
            ) : (
              <>
                <button
                  className={`ai-chat-input__voice ${isRecording ? 'recording' : ''}`}
                  onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                  disabled={isLoading}
                  title={isRecording ? t('aiChat.cancelRecording', 'Остановить запись') : t('aiChat.voiceInput', 'Записать голос')}
                >
                  <Icon name={isRecording ? 'square' : 'mic'} size={20} />
                </button>
                <button
                  className="ai-chat-input__send"
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading || isRecording}
                >
                  <Icon name="arrow-up" size={20} />
                </button>
              </>
            )}
          </div>
        </Card>
      </div>
      </div>
    </PremiumFeature>
  );
};
